import { FormattedMessage, FormattedNumber } from 'react-intl';
import moment from 'moment';
import { useTheme } from '@mui/material/styles';
import { getRouteApi, useNavigate } from '@tanstack/react-router';

import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import DownloadIcon from '@mui/icons-material/CloudDownload';

import Spacings from '~/modules/ui-kit/spacings';
import { useMonthReports } from '~/hooks/api/useMonthReports';
import { useMonthReport } from '~/hooks/api/useMonthReport';
import messages from './messages';

const route = getRouteApi('/reports/$reportType');

const MonthlyReport = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const search = route.useSearch();

  const year = search.year ?? 0;
  const month = search.month ?? 0;

  const { data: availableReports = [], isLoading: isFetchingAvailableReports } =
    useMonthReports();
  const { data: monthReport, isLoading: isFetching } = useMonthReport({
    year,
    month,
  });

  const prepareData = () => {
    if (!monthReport) return [];

    return [
      {
        label: <FormattedMessage {...messages.participatingPioneers} />,
        key: 'participatingPioneers',
        value: monthReport.participatingPioneers ?? 0,
      },
      {
        label: <FormattedMessage {...messages.participatingPublishers} />,
        key: 'participatingPublishers',
        value: monthReport.participatingPublishers ?? 0,
      },
      {
        label: <FormattedMessage {...messages.missingReports} />,
        key: 'missingReports',
        value: monthReport.missingReports ?? 0,
      },
      {
        label: <FormattedMessage {...messages.publisherHours} />,
        key: 'publisherHours',
        value: monthReport.publisherHours ?? 0,
      },
    ];
  };

  const handleSelectMonth = (event: SelectChangeEvent<number>) => {
    navigate({
      to: '/reports/$reportType',
      params: { reportType: 'month' },
      search: { year, month: Number(event.target.value), quarter: undefined },
    });
  };

  const handleSelectYear = (event: SelectChangeEvent<number>) => {
    navigate({
      to: '/reports/$reportType',
      params: { reportType: 'month' },
      search: { year: Number(event.target.value), month, quarter: undefined },
    });
  };

  const handleDownloadReportData = () => {
    const url = `/api/Analytics/turnReports/${year}/${month}`;
    window.open(url, '_blank', 'noreferrer');
  };

  const selectedMonthLabel = moment.months()[month - 1];

  return (
    <Spacings.Inset>
      <Spacings.Stack>
        <Spacings.Inline>
          {!isFetchingAvailableReports && (
            <>
              <FormControl variant="standard" style={{ minWidth: 120 }}>
                <InputLabel id="year-select-label">
                  <FormattedMessage {...messages.year} />
                </InputLabel>
                <Select
                  variant="standard"
                  labelId="year-select-label"
                  id="year-select"
                  value={year}
                  onChange={handleSelectYear}
                >
                  {availableReports
                    .reduce<number[]>((allYears, report) => {
                      if (
                        report.year !== undefined &&
                        !allYears.includes(report.year)
                      ) {
                        return [...allYears, report.year];
                      }
                      return allYears;
                    }, [])
                    .map((yearOption) => (
                      <MenuItem key={yearOption} value={yearOption}>
                        {yearOption}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <FormControl
                variant="standard"
                style={{
                  minWidth: 120,
                  marginLeft: theme.spacing(2),
                }}
              >
                <InputLabel id="month-select-label">
                  <FormattedMessage {...messages.month} />
                </InputLabel>
                <Select
                  variant="standard"
                  labelId="month-select-label"
                  id="month-select"
                  value={month}
                  onChange={handleSelectMonth}
                >
                  {availableReports
                    .filter((report) => report.year === year)
                    .map((report) => (
                      <MenuItem
                        key={report.monthInYear}
                        value={report.monthInYear}
                      >
                        {report.monthInYear}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </>
          )}
        </Spacings.Inline>

        {!isFetching && (
          <TableContainer style={{ marginTop: theme.spacing(2) }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ paddingLeft: 0 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadReportData}
                    >
                      <FormattedMessage {...messages.downloadReport} />
                    </Button>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" color="textSecondary">
                      {selectedMonthLabel}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prepareData().map(({ label, value, key }) => (
                  <TableRow key={key}>
                    <TableCell>
                      <Typography color="textSecondary">{label}</Typography>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                      <Typography>
                        <FormattedNumber value={value} />
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Spacings.Stack>
    </Spacings.Inset>
  );
};

MonthlyReport.displayName = 'MonthlyReport';

export default MonthlyReport;
